import { useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ConsentProgramHistory } from "@/Common/Models/Patient/ConsentProgramHistory";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ConsentStatus } from "@/Patient/Models/Consent/ConsentEnum";
import { ConsentProgramDialog } from "../../Consent/ConsentProgramDialog";
import { PatientConsentProgramHistory } from "./PatientConsentProgramHistory";

import styles from "./PatientConsentPrograms.module.scss";

interface IPatientConsentProgramsProps {
    patientContactId: string;
    items: Immutable.List<ConsentProgramHistory>;
    readOnly?: boolean;
    isHistory?: boolean;
}

function PatientConsentPrograms({ patientContactId, items, readOnly, isHistory }: IPatientConsentProgramsProps): JSX.Element {
    const [showConsentProgramHistoryDialog, setShowConsentProgramHistoryDialog] = useState(false);
    const [showConsentProgramDialog, setShowConsentProgramDialog] = useState(false);
    const [selectedConsentProgramCode, setSelectedConsentProgramCode] = useState(null);
    const [defaultConsentOption, setDefaultConsentOption] = useState(null);
    const [filteredConsentProgramHistory, setFilteredConsentProgramHistory] = useState([].toImmutable());
    const { getText } = useTranslation();
    var patient: Patient = patientStore.getCurrentPatient();
    var person = Person.FromPatient(patient);

    if (items.count() === 0) {
        return <></>;
    }

    const handleProgramClick = (consentPrograms: ConsentProgramHistory[], code: string): void => {
        const filteredConsentProgramHistory = consentPrograms.filter(item => item.consentProgramCode === code);
        setFilteredConsentProgramHistory(filteredConsentProgramHistory.toImmutable());
        setShowConsentProgramHistoryDialog(true);
    };

    const addConsent = (code: string, status: ConsentStatus): void => {
        setSelectedConsentProgramCode(code);
        setDefaultConsentOption(status);
        setShowConsentProgramDialog(true);
    };

    const savePatientConsent = (): void => {
        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_LoadConsentProgramHistory, { PatientId: patientContactId });
        setShowConsentProgramDialog(false);
    };

    const closeConsentProgramDialog = (): void => {
        setShowConsentProgramDialog(false);
    };

    const sortedConsentPrograms = items.toArray()
        .sortByProperties(
            propertyComparer(x => x.consentProgramName),
            propertyComparerDesc(x => x.consentDate)
        );

    const consentPrograms = isHistory ?
        sortedConsentPrograms
        :
        sortedConsentPrograms.groupBy(item => item.consentProgramCode)
            .map(item => item[0]);

    let render = consentPrograms.map(consent => (
        <tr key={consent.key}>
            <td>
                {isHistory && consent.consentProgramName}
                {!isHistory && (
                    <a onClick={() => handleProgramClick(sortedConsentPrograms, consent.consentProgramCode)}>
                        {consent.consentProgramName}
                    </a>
                )}
            </td>
            <td>
                {consent.isDocumentationGiven === true && getText("YesLabel")}
                {consent.isDocumentationGiven === false && getText("NoLabel")}
            </td>
            <td>
                {consent.consented && getText("ConsentedLabel")}
                {consent.refused && getText("RefusedOrRevokedLabel")}
                {consent.didNotRespond && getText("DidntRespondLabel")}
            </td>
            <td>
                {consent.givenToName}
            </td>
            <td>
                {consent.isGivenByPatient && getText("PatientLabel")}
                {!consent.isGivenByPatient && getText("PatientRepresentativeContactLabel")}
            </td>
            <td>
                {consent.consentDate && LuxonDateTimeParser.parse(consent.consentDate).toFormat("yyyy-MM-dd")}
            </td>
            {!readOnly && (
                <td>
                    <a onClick={() => addConsent(consent.consentProgramCode, ConsentStatus.Consented)}>{getText("ModifyLabel")}</a>
                    <span className={styles.actionSeparator}>|</span>
                    <a className={styles.actionRemove} onClick={() => addConsent(consent.consentProgramCode, ConsentStatus.Refused)}>{getText("RemoveLabel")}</a>
                </td>
            )}
        </tr>
    ));

    return (
        <>
            <div className="psingle-column centered-consent">
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{getText("Consent_ProgramLabel")}</th>
                            <th>{getText("Consent_DocumentationGivenLabel")}</th>
                            <th>{getText("Consent_StatusLabel")}</th>
                            <th>{getText("Consent_ConsentGivenToLabel")}</th>
                            <th>{getText("Consent_ConsentGivenByLabel")}</th>
                            <th>{getText("Consent_DateLabel")}</th>
                            {!readOnly && (
                                <th>&nbsp;</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {render}
                    </tbody>
                </table>
            </div>
            {showConsentProgramHistoryDialog && (
                <PatientConsentProgramHistory
                    patientContactId={patientContactId}
                    items={filteredConsentProgramHistory}
                    onClose={() => setShowConsentProgramHistoryDialog(false)}
                />
            )}
            {showConsentProgramDialog && (
                <ConsentProgramDialog
                    person={person}
                    onSave={() => savePatientConsent()}
                    onClose={() => closeConsentProgramDialog()}
                    code={selectedConsentProgramCode}
                    showAccessWithoutConsent={false}
                    defaultOption={defaultConsentOption}
                />
            )}
        </>
    );
}

export { PatientConsentPrograms };
